import {
  AmendmentType,
  TermContractLine,
  TerminationData,
  amendment,
} from '../../slice/types';

type TerminationAmendmentData = TerminationData & {
  action?: string;
  oldTerminationDate?: string;
  newTerminationDate?: string;
};

const isBlank = (value?: string) => !value?.trim();

const isRecalledTermination = (data: TerminationAmendmentData) =>
  (data.action ?? 'normal').toLowerCase() === 'recalled';

/** Nearest prior termination amendment; BI / other types between are ignored. */
export const findPriorTerminationAmendment = (
  amendments: amendment[],
  currentIndex: number,
): TerminationAmendmentData | null => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    const item = amendments[i];
    if (item.type !== AmendmentType.TERMINATION) {
      continue;
    }
    return item.data as TerminationAmendmentData;
  }
  return null;
};

const mergeProductOldTerminationDates = (
  products: TermContractLine[],
  recalledProducts: TermContractLine[],
): TermContractLine[] => {
  const oldDateByLineId = new Map(
    recalledProducts.map(line => [line.contractLineId, line.oldTerminationDate]),
  );

  return products.map(product => {
    if (!isBlank(product.oldTerminationDate)) {
      return product;
    }
    const recalledOldDate = oldDateByLineId.get(product.contractLineId);
    if (isBlank(recalledOldDate)) {
      return product;
    }
    return { ...product, oldTerminationDate: recalledOldDate! };
  });
};

export const enrichTerminationAfterRecall = (
  termination: TerminationAmendmentData,
  priorTermination: TerminationAmendmentData | null,
): TerminationAmendmentData => {
  if (
    !priorTermination ||
    !isRecalledTermination(priorTermination) ||
    isRecalledTermination(termination)
  ) {
    return termination;
  }

  const enriched: TerminationAmendmentData = { ...termination };

  if (isBlank(enriched.oldTerminationDate) && !isBlank(priorTermination.oldTerminationDate)) {
    enriched.oldTerminationDate = priorTermination.oldTerminationDate;
  }

  if (termination.products?.length) {
    enriched.products = mergeProductOldTerminationDates(
      termination.products,
      priorTermination.products ?? [],
    );
  }

  return enriched;
};

export const enrichTerminationAmendmentData = (
  amendments: amendment[],
  currentIndex: number,
  termination: TerminationAmendmentData,
): TerminationAmendmentData =>
  enrichTerminationAfterRecall(
    termination,
    findPriorTerminationAmendment(amendments, currentIndex),
  );
